import { useCallback, useEffect, useRef, useState } from 'react';
import ActivitySidebar from '../components/spark-activities/ActivitySidebar';
import ActivityEditPanel from '../components/spark-activities/ActivityEditPanel';
import ActivityCreatePanel from '../components/spark-activities/ActivityCreatePanel';
import ActivityPreviewPanel from '../components/activity-preview/ActivityPreviewPanel';
import BatchUploadDialog from '../components/spark-activities/BatchUploadDialog';
import UnsavedChangesModal from '../components/spark-activities/UnsavedChangesModal';
import DeleteConfirmationModal from '../components/spark-activities/DeleteConfirmationModal';
import { useSparkActivities, useSparkActivity, useDeleteSparkActivity, useDeleteSparkActivityBatch, useDownloadSparkActivityCsv } from '../hooks/useSparkActivities';
import { useSparkActivityFilters } from '../hooks/useSparkActivityFilters';
import type { UpdateSparkActivityPayload, CreateSparkActivityPayload } from '../types/spark-activity';

export default function SparkActivityManagePage() {
  const { filters, updateFilter, clearFilters } = useSparkActivityFilters();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [pendingId, setPendingId] = useState<number | null>(null);
  const isDirtyRef = useRef(false);

  const [deleteTarget, setDeleteTarget] = useState<{ id: number; title: string } | null>(null);
  const deleteActivity = useDeleteSparkActivity();

  const [deleteBatchTarget, setDeleteBatchTarget] = useState<{ batchName: string; batchId: number; count: number } | null>(null);
  const deleteBatch = useDeleteSparkActivityBatch();

  const downloadCsv = useDownloadSparkActivityCsv();

  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<UpdateSparkActivityPayload | null>(null);
  const [createFormData, setCreateFormData] = useState<CreateSparkActivityPayload | null>(null);
  const [focusedStage, setFocusedStage] = useState<number | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isWideEnough, setIsWideEnough] = useState(
    () => typeof window !== 'undefined' ? window.matchMedia('(min-width: 1200px)').matches : true
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1200px)');
    const handler = (e: MediaQueryListEvent) => setIsWideEnough(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const showPreview = previewVisible && isWideEnough;

  const { data: listData, isLoading } = useSparkActivities(filters);
  const activities = listData?.items ?? [];

  const selectedIndex = selectedId !== null ? activities.findIndex((a) => a.id === selectedId) : -1;
  const { data: selectedActivity } = useSparkActivity(selectedId);

  const handleDirtyChange = useCallback((dirty: boolean) => {
    isDirtyRef.current = dirty;
  }, []);

  const handleSelect = (id: number) => {
    if (id === selectedId && !isCreating) return;
    if (isDirtyRef.current) {
      setPendingId(id);
    } else {
      setIsCreating(false);
      setSelectedId(id);
    }
  };

  const handlePrev = () => {
    if (selectedIndex > 0) setSelectedId(activities[selectedIndex - 1].id);
  };

  const handleNext = () => {
    if (selectedIndex < activities.length - 1) setSelectedId(activities[selectedIndex + 1].id);
  };

  const handleNewClick = () => {
    if (isDirtyRef.current) {
      setPendingId(null);
      return;
    }
    setSelectedId(null);
    setIsCreating(true);
  };

  const handleCreated = (id: number) => {
    setIsCreating(false);
    isDirtyRef.current = false;
    setSelectedId(id);
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    isDirtyRef.current = false;
  };

  const handleDeleteClick = (id: number) => {
    const activity = activities.find(a => a.id === id);
    if (activity) {
      setDeleteTarget({ id: activity.id, title: activity.title });
    }
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    deleteActivity.mutate(deleteTarget.id, {
      onSuccess: () => {
        if (selectedId === deleteTarget.id) setSelectedId(null);
        setDeleteTarget(null);
      },
      onSettled: () => {
        setDeleteTarget(null);
      },
    });
  };

  const handleDeleteBatchClick = (batchName: string, batchId: number, count: number) => {
    setDeleteBatchTarget({ batchName, batchId, count });
  };

  const handleDeleteBatchConfirm = () => {
    if (!deleteBatchTarget) return;
    deleteBatch.mutate(deleteBatchTarget.batchId, {
      onSuccess: () => {
        if (selectedId && activities.find(a => a.id === selectedId)?.batchId === deleteBatchTarget.batchId) {
          setSelectedId(null);
        }
        setDeleteBatchTarget(null);
      },
      onSettled: () => {
        setDeleteBatchTarget(null);
      },
    });
  };

  return (
    <div className="flex h-[calc(100vh-64px)] gap-[22px] p-[22px]">
      <ActivitySidebar
        filters={filters}
        onUpdateFilter={updateFilter}
        onClearFilters={clearFilters}
        activities={activities}
        selectedId={selectedId}
        onSelect={handleSelect}
        onDelete={handleDeleteClick}
        onDeleteBatch={handleDeleteBatchClick}
        isLoading={isLoading}
        onUploadClick={() => setUploadDialogOpen(true)}
        onDownloadClick={() => downloadCsv.mutate()}
        onNewClick={handleNewClick}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
      />

      <main className="flex-1 overflow-y-auto bg-pine-paper min-w-0">
        {isCreating ? (
          <ActivityCreatePanel
            formData={createFormData}
            setFormData={setCreateFormData}
            focusedStage={focusedStage}
            onFocusedStageChange={setFocusedStage}
            onTogglePreview={() => setPreviewVisible((v) => !v)}
            previewVisible={previewVisible}
            onCancel={handleCancelCreate}
            onCreated={handleCreated}
          />
        ) : selectedActivity ? (
          <ActivityEditPanel
            activity={selectedActivity}
            currentIndex={selectedIndex}
            total={activities.length}
            onPrev={handlePrev}
            onNext={handleNext}
            onDirtyChange={handleDirtyChange}
            onArchived={() => setSelectedId(null)}
            formData={formData}
            setFormData={setFormData}
            focusedStage={focusedStage}
            onFocusedStageChange={setFocusedStage}
            onTogglePreview={() => setPreviewVisible((v) => !v)}
            previewVisible={previewVisible}
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-pine-ink-soft">
              <p className="text-sm">Select an activity to view details</p>
              {activities.length === 0 && !isLoading && (
                <p className="text-xs mt-2">
                  Upload a CSV to get started
                </p>
              )}
            </div>
          </div>
        )}
      </main>

      <aside
        className="flex-shrink-0 flex flex-col items-center overflow-y-auto transition-all duration-200 ease-out"
        style={{
          width: showPreview ? '280px' : '0px',
          opacity: showPreview ? 1 : 0,
          paddingTop: showPreview ? '16px' : '0px',
          overflow: showPreview ? 'auto' : 'hidden',
        }}
      >
        {showPreview && (
          <ActivityPreviewPanel
            formData={isCreating ? createFormData : formData}
            focusedStage={focusedStage}
            onStageChange={setFocusedStage}
          />
        )}
      </aside>

      <BatchUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
      />

      <UnsavedChangesModal
        open={pendingId !== null}
        onLeave={() => {
          const id = pendingId;
          setPendingId(null);
          isDirtyRef.current = false;
          if (id !== null) setSelectedId(id);
        }}
        onStay={() => setPendingId(null)}
      />

      <DeleteConfirmationModal
        open={deleteTarget !== null}
        activityTitle={deleteTarget?.title ?? ''}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        isLoading={deleteActivity.isPending}
      />

      <DeleteConfirmationModal
        open={deleteBatchTarget !== null}
        activityTitle={`batch "${deleteBatchTarget?.batchName}" (${deleteBatchTarget?.count} activities)`}
        onClose={() => setDeleteBatchTarget(null)}
        onConfirm={handleDeleteBatchConfirm}
        isLoading={deleteBatch.isPending}
      />
    </div>
  );
}
